import { useState, useEffect, memo } from "react";
import { View, type StyleProp, type ImageStyle } from "react-native";
import { Image, type ImageContentFit } from "expo-image";
import { useUnistyles } from "react-native-unistyles";

export interface LegacySnapshotImageProps {
  /** Camera key for cache key generation */
  cameraKey: string;
  /** Snapshot URL from camera API */
  thumbnailUrl: string | null | undefined;
  /** Snapshot captured timestamp for cache invalidation */
  capturedAt: string | null | undefined;
  /** Style for the container */
  style?: StyleProp<ImageStyle>;
  /** How the image should fit within its container */
  contentFit?: ImageContentFit;
}

/**
 * Legacy snapshot image component that handles error states, placeholders,
 * and cache key behaviour from the old camera.snapshot API response.
 */
export const LegacySnapshotImage = memo(function LegacySnapshotImage({
  cameraKey,
  thumbnailUrl,
  capturedAt,
  style,
  contentFit = "cover",
}: LegacySnapshotImageProps) {
  const { theme } = useUnistyles();
  const [imageError, setImageError] = useState(false);

  // Reset image error when camera changes (important for recycled list items)
  useEffect(() => {
    setImageError(false);
  }, [cameraKey, thumbnailUrl, capturedAt]);

  const imageSource = thumbnailUrl || "";
  const shouldShowImage = imageSource && !imageError;

  // Cache key based on capturedAt timestamp, or 5-minute bucket if not available
  const snapshotCacheKey =
    capturedAt ? `${cameraKey}:${capturedAt}` : `${cameraKey}:${Math.floor(Date.now() / (5 * 60 * 1000))}`;

  if (!shouldShowImage) {
    return <View style={[{ width: "100%", height: "100%", backgroundColor: theme.colors.borderCard }, style]} />;
  }

  return (
    <Image
      key={cameraKey}
      source={{ uri: imageSource, cacheKey: snapshotCacheKey } as any}
      style={[{ width: "100%", height: "100%" }, style]}
      contentFit={contentFit}
      cachePolicy="memory-disk"
      onError={() => setImageError(true)}
    />
  );
});
